import React, { useEffect, useRef } from "react";

// Beach ride clip: public/hero.mp4 (copied from E-bike_with_thicker_tires_beach_202605210301.mp4)
const VIDEO_URL = "/hero.mp4";

export const Hero = () => {
  const videoRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const FADE = 0.5; // seconds — fade-in/out window per loop

    const tick = () => {
      const v = videoRef.current;
      if (!v) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const t = v.currentTime;
      const d = v.duration;
      let opacity = 1;
      if (Number.isFinite(d) && d > 0) {
        if (t < FADE) opacity = Math.max(0, Math.min(1, t / FADE));
        else if (d - t < FADE) opacity = Math.max(0, Math.min(1, (d - t) / FADE));
        else opacity = 1;
      } else {
        opacity = 1;
      }
      v.style.opacity = String(opacity);
      rafRef.current = requestAnimationFrame(tick);
    };

    const handleError = () => {
      const v = videoRef.current;
      if (!v) return;
      v.style.opacity = "1";
    };

    video.addEventListener("error", handleError);

    video.muted = true;
    const p = video.play();
    if (p && typeof p.catch === "function") p.catch(() => {});

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      video.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden"
    >
      <video
        ref={videoRef}
        data-testid="hero-video"
        className="absolute top-0 left-0 h-full w-[125%] max-w-none object-cover object-[28%_center] md:w-[112%] md:object-left"
        src={VIDEO_URL}
        loop
        muted
        playsInline
        autoPlay
        preload="auto"
        aria-hidden="true"
      />

      {/* Light scrim so black type stays readable on bright sand/sky */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/85 via-white/50 to-transparent"
      />

      <div
        data-testid="hero-content"
        className="relative z-10 flex min-h-[100svh] items-center px-8 sm:px-12 lg:px-20 xl:px-24 py-28 sm:py-32"
      >
        <div className="w-full max-w-[760px] text-black">
          <p
            data-testid="hero-eyebrow"
            className="animate-fade-rise text-xs sm:text-[13px] uppercase tracking-[0.32em] text-black/70 mb-10"
          >
            Los Angeles · Coastal E-Bike Rentals
          </p>

          <h1
            data-testid="hero-headline"
            className="font-display animate-fade-rise font-normal text-black"
            style={{
              fontSize: "clamp(3rem, 6.6vw, 7.25rem)",
              lineHeight: 0.95,
              letterSpacing: "-2px",
            }}
          >
            Ride to{" "}
            <span className="italic text-black/55">Paradise</span>
          </h1>

          <p
            data-testid="hero-description"
            className="animate-fade-rise-delay text-base sm:text-lg max-w-xl mt-10 leading-relaxed text-black/75"
          >
            Paradise Worldwide rents premium folding e-bikes for the people who
            believe Los Angeles is best seen at fifteen miles per hour. Skip the
            traffic. Find the ocean. Ride for hours.
          </p>

          <a
            href="#reach"
            data-testid="hero-cta-begin-journey"
            className="animate-fade-rise-delay-2 btn-pill inline-flex mt-12 px-12 py-4 sm:py-5 text-base"
          >
            Begin Your Journey
          </a>
        </div>
      </div>
    </section>
  );
};

export default Hero;
